import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions/search/search';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import type { NewsCardItem } from 'volto-ministerio-publico-federal/components/UI';
import {
  NEWS_PORTAL_TYPES,
  NEWS_PUBLIC_STATES,
} from 'volto-ministerio-publico-federal/constants';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import { decodeVocabularyToken } from 'volto-ministerio-publico-federal/utils/decodeVocabularyToken';
import { getVocabularyName } from 'volto-ministerio-publico-federal/config/vocabulary-config';

interface UseNewsByThemeOptions {
  tema?: string;
  unidade?: string;
  limit: number;
  blockId: string;
  /**
   * `@id` de uma notícia a remover do resultado — na página de notícia, ela
   * própria. A busca não sabe excluir por caminho, então pedimos um item a mais
   * e descartamos aqui; assim ainda sobram `limit` cards.
   */
  excludeId?: string;
}

interface UseNewsByThemeResult {
  items: NewsCardItem[];
  loading: boolean;
  loaded: boolean;
}

const METADATA_FIELDS = [
  'tema',
  'unidadeOrigem',
  'unit_title',
  'effective',
  'image_scales',
  'image_field',
  'description',
  'tituloAlternativo',
];

export const getSubrequestKey = (blockId: string) => `news-by-theme-${blockId}`;

const ORIGIN_UNITS_VOCAB = getVocabularyName('ORIGIN_UNITS');

export function useNewsByTheme({
  tema,
  unidade,
  limit,
  blockId,
  excludeId,
}: UseNewsByThemeOptions): UseNewsByThemeResult {
  const dispatch = useDispatch();
  const subrequestKey = getSubrequestKey(blockId);
  const batchSize = excludeId ? limit + 1 : limit;

  // O índice `tema` guarda o valor do termo, não o token — ver
  // `decodeVocabularyToken`. Sem isto, todo tema acentuado busca vazio.
  const temaValue = decodeVocabularyToken(tema);

  useEffect(() => {
    if (!temaValue) {
      return;
    }

    dispatch(
      searchContent(
        '',
        {
          portal_type: NEWS_PORTAL_TYPES,
          review_state: NEWS_PUBLIC_STATES,
          tema: temaValue,
          ...(unidade ? { unidadeOrigem: unidade } : {}),
          sort_on: 'effective',
          sort_order: 'descending',
          b_size: batchSize,
          metadata_fields: METADATA_FIELDS,
        },
        subrequestKey,
      ) as any,
    );
  }, [dispatch, temaValue, unidade, batchSize, subrequestKey]);

  const subrequest = useSelector(
    (state: any) => state.search?.subrequests?.[subrequestKey],
  );

  // Sem o `?? []` aqui: um literal novo a cada render invalidaria o `useMemo`
  // abaixo e devolveria uma lista com identidade nova toda vez.
  const rawItems = subrequest?.items as NewsCardItem[] | undefined;

  // O `@search` devolve `unidadeOrigem` como sigla ("prse") e `unit_title`
  // vazio, então o card mostraria a sigla crua. O vocabulário de unidades de
  // origem dá o nome por extenso; resolvemos a sigla para `{token, title}`,
  // formato que `getLocation` (UI/NewsCardRow) já entende. `size: -1` traz o
  // vocabulário inteiro — o batch padrão de 25 deixa de fora as siglas do fim
  // do alfabeto (ex.: "prse").
  useEffect(() => {
    dispatch(
      getVocabulary({ vocabNameOrURL: ORIGIN_UNITS_VOCAB, size: -1 }) as any,
    );
  }, [dispatch]);

  // No reducer padrão do Volto os itens ficam em
  // `state.vocabularies[nome].items` como `{ value: token, label: title }`.
  const originUnits = useSelector(
    (state: any) => state.vocabularies?.[ORIGIN_UNITS_VOCAB]?.items,
  ) as Array<{ value: string; label: string }> | undefined;

  const unitTitleByToken = useMemo(() => {
    const map = new Map<string, string>();
    (originUnits || []).forEach((unit) => {
      if (unit?.value && unit?.label) {
        map.set(unit.value, unit.label);
      }
    });
    return map;
  }, [originUnits]);

  const items = useMemo(() => {
    const list = rawItems ?? [];

    const excluded = excludeId ? flattenToAppURL(excludeId) : null;

    return list
      .filter(
        (item) => !excluded || flattenToAppURL(item['@id'] || '') !== excluded,
      )
      .map((item) => {
        if (typeof item.unidadeOrigem !== 'string') {
          return item;
        }

        const title = unitTitleByToken.get(item.unidadeOrigem);
        return title
          ? { ...item, unidadeOrigem: { token: item.unidadeOrigem, title } }
          : item;
      });
  }, [rawItems, excludeId, unitTitleByToken]);

  return {
    items,
    loading: subrequest?.loading ?? false,
    loaded: subrequest?.loaded ?? false,
  };
}

export default useNewsByTheme;
